import { CHIPS_PER_CASH_UNIT, DEFAULT_CASH_IN, DEFAULT_CHIPS } from "@/lib/constants";
import {
  decimalSeparator,
  detectChipUnit,
  detectExactChipUnit,
  formatCashValue,
  formatChipValue,
  formatNumber,
  parseAmount,
} from "@/lib/formatting";
import { normalizeCashInput, normalizeChipInput, sanitizeCashEditInput, sanitizeChipInput } from "@/lib/parsing";
import { useMemo, useState } from "react";

export interface PlayerDraft {
  id: string;
  name: string;
  cashIn: string;
  chipsBought: string;
  finalChips: string;
}

export interface HiddenFinalPlayer {
  id: string;
  previousFinalChips: string;
}

interface ChipEditState {
  playerId: string;
  field: "chipsBought" | "finalChips";
  value: string;
}

interface CashEditState {
  playerId: string;
  value: string;
}

function makePlayer(name: string, cashIn = DEFAULT_CASH_IN, chipsBought = DEFAULT_CHIPS, finalChips = chipsBought): PlayerDraft {
  return {
    id: crypto.randomUUID(),
    name,
    cashIn: formatNumber(cashIn),
    chipsBought: formatNumber(chipsBought),
    finalChips: formatNumber(finalChips),
  };
}

function mostCommonValue(players: PlayerDraft[], field: "cashIn" | "chipsBought", fallback: number): number {
  const counts = new Map<number, number>();
  for (const player of players) {
    const value = parseAmount(player[field]);
    if (value === null || value < 0) {
      continue;
    }
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  if (counts.size === 0) {
    return fallback;
  }
  let bestValue = fallback;
  let bestCount = -1;
  for (const [value, count] of counts.entries()) {
    if (count > bestCount || (count === bestCount && value < bestValue)) {
      bestValue = value;
      bestCount = count;
    }
  }
  return bestValue;
}

export interface UsePlayersStateReturn {
  players: PlayerDraft[];
  visibleFinalPlayers: PlayerDraft[];
  hiddenFinalPlayers: HiddenFinalPlayer[];
  hiddenFinalIds: Set<string>;
  pendingPlayerName: string;
  setPendingPlayerName: (name: string) => void;
  commitPendingPlayer: () => void;
  removePlayer: (id: string) => void;
  updatePlayerName: (id: string, name: string) => void;
  cashInputValue: (playerId: string, storedRawValue: string) => string;
  beginCashEdit: (playerId: string, currentRawValue: string) => void;
  changeCashEdit: (playerId: string, nextValue: string) => void;
  endCashEdit: () => void;
  chipInputValue: (playerId: string, field: "chipsBought" | "finalChips", storedRawValue: string) => string;
  beginChipEdit: (playerId: string, field: "chipsBought" | "finalChips", currentRawValue: string) => void;
  changeChipEdit: (playerId: string, field: "chipsBought" | "finalChips", nextValue: string) => void;
  endChipEdit: (playerId: string, field: "chipsBought" | "finalChips") => void;
  hidePlayerFromFinalList: (id: string) => void;
  restorePlayerToFinalList: (id: string) => void;
  totalsPreview: { cashIn: number; chipsBought: number; finalChips: number };
  suggestedCashIn: number;
  suggestedChipsBought: number;
  chipValueDisplayUnit: number;
  exactChipValueUnit: number;
}

export function usePlayersState(): UsePlayersStateReturn {
  const [players, setPlayers] = useState<PlayerDraft[]>([makePlayer("Alice"), makePlayer("Bob")]);
  const [manualChipsPlayers, setManualChipsPlayers] = useState<Set<string>>(new Set());
  const [manualFinalChipsPlayers, setManualFinalChipsPlayers] = useState<Set<string>>(new Set());
  const [hiddenFinalPlayers, setHiddenFinalPlayers] = useState<HiddenFinalPlayer[]>([]);
  const [pendingPlayerName, setPendingPlayerName] = useState("");
  const [chipEditState, setChipEditState] = useState<ChipEditState | null>(null);
  const [cashEditState, setCashEditState] = useState<CashEditState | null>(null);

  const hiddenFinalIds = useMemo(() => new Set(hiddenFinalPlayers.map((p) => p.id)), [hiddenFinalPlayers]);
  const visibleFinalPlayers = useMemo(() => players.filter((p) => !hiddenFinalIds.has(p.id)), [players, hiddenFinalIds]);

  const totalsPreview = useMemo(() => {
    return players.reduce(
      (acc, player) => {
        const cashIn = parseAmount(player.cashIn);
        const chipsBought = parseAmount(player.chipsBought);
        const finalChips = hiddenFinalIds.has(player.id) ? 0 : parseAmount(player.finalChips);
        acc.cashIn += cashIn ?? 0;
        acc.chipsBought += chipsBought ?? 0;
        acc.finalChips += finalChips ?? 0;
        return acc;
      },
      { cashIn: 0, chipsBought: 0, finalChips: 0 },
    );
  }, [players, hiddenFinalIds]);

  const suggestedCashIn = useMemo(() => mostCommonValue(players, "cashIn", DEFAULT_CASH_IN), [players]);
  const suggestedChipsBought = useMemo(() => mostCommonValue(players, "chipsBought", DEFAULT_CHIPS), [players]);

  const { chipValueDisplayUnit, exactChipValueUnit } = useMemo(() => {
    const relevantChipAmounts = players.flatMap((player) => {
      const bought = parseAmount(player.chipsBought);
      const finalChips = hiddenFinalIds.has(player.id) ? 0 : parseAmount(player.finalChips);
      return [bought ?? 0, finalChips ?? 0];
    });
    return {
      chipValueDisplayUnit: detectChipUnit(relevantChipAmounts),
      exactChipValueUnit: detectExactChipUnit(relevantChipAmounts),
    };
  }, [players, hiddenFinalIds]);

  // --- Helpers ---

  const updatePlayer = (playerId: string, field: keyof Omit<PlayerDraft, "id">, value: string) => {
    setPlayers((current) => current.map((p) => (p.id === playerId ? { ...p, [field]: value } : p)));
  };

  const updatePlayerName = (id: string, name: string) => {
    updatePlayer(id, "name", name);
  };

  // --- Cash editing ---

  const updatePlayerCashIn = (playerId: string, value: string) => {
    const normalizedCashIn = normalizeCashInput(value);
    setPlayers((current) =>
      current.map((player) => {
        if (player.id !== playerId) {
          return player;
        }
        if (manualChipsPlayers.has(playerId)) {
          return { ...player, cashIn: normalizedCashIn };
        }
        const parsedCashIn = parseAmount(normalizedCashIn);
        const autoChipsBought =
          parsedCashIn === null || parsedCashIn < 0 ? "" : String(Math.round(parsedCashIn * CHIPS_PER_CASH_UNIT));
        const autoFinalChips = manualFinalChipsPlayers.has(playerId) ? player.finalChips : autoChipsBought;
        return { ...player, cashIn: normalizedCashIn, chipsBought: autoChipsBought, finalChips: autoFinalChips };
      }),
    );
  };

  const beginCashEdit = (playerId: string, currentRawValue: string) => {
    setCashEditState({
      playerId,
      value: currentRawValue ? currentRawValue.replace(".", decimalSeparator) : "",
    });
  };

  const changeCashEdit = (playerId: string, nextValue: string) => {
    const sanitized = sanitizeCashEditInput(nextValue);
    setCashEditState({ playerId, value: sanitized });
    updatePlayerCashIn(playerId, sanitized);
  };

  const endCashEdit = () => {
    setCashEditState(null);
  };

  const cashInputValue = (playerId: string, storedRawValue: string) => {
    if (cashEditState && cashEditState.playerId === playerId) {
      return cashEditState.value;
    }
    return formatCashValue(storedRawValue);
  };

  // --- Chip editing ---

  const updatePlayerChips = (playerId: string, field: "chipsBought" | "finalChips", value: string) => {
    const normalized = normalizeChipInput(value);
    if (field === "chipsBought") {
      setPlayers((current) =>
        current.map((player) => {
          if (player.id !== playerId) {
            return player;
          }
          return {
            ...player,
            chipsBought: normalized,
            finalChips: manualFinalChipsPlayers.has(playerId) ? player.finalChips : normalized,
          };
        }),
      );
      return;
    }
    updatePlayer(playerId, field, normalized);
  };

  const beginChipEdit = (playerId: string, field: "chipsBought" | "finalChips", currentRawValue: string) => {
    setChipEditState({ playerId, field, value: sanitizeChipInput(currentRawValue) });
  };

  const changeChipEdit = (playerId: string, field: "chipsBought" | "finalChips", nextValue: string) => {
    const sanitized = sanitizeChipInput(nextValue);
    if (field === "chipsBought") {
      setManualChipsPlayers((current) => {
        if (current.has(playerId)) return current;
        const next = new Set(current);
        next.add(playerId);
        return next;
      });
    } else {
      setManualFinalChipsPlayers((current) => {
        if (current.has(playerId)) return current;
        const next = new Set(current);
        next.add(playerId);
        return next;
      });
    }
    setChipEditState({ playerId, field, value: sanitized });
    updatePlayerChips(playerId, field, sanitized);
  };

  const endChipEdit = (playerId: string, field: "chipsBought" | "finalChips") => {
    if (chipEditState && chipEditState.playerId === playerId && chipEditState.field === field) {
      updatePlayerChips(playerId, field, chipEditState.value);
    }
    setChipEditState(null);
  };

  const chipInputValue = (playerId: string, field: "chipsBought" | "finalChips", storedRawValue: string) => {
    if (chipEditState && chipEditState.playerId === playerId && chipEditState.field === field) {
      return chipEditState.value;
    }
    return formatChipValue(storedRawValue);
  };

  // --- Player CRUD ---

  const commitPendingPlayer = () => {
    const name = pendingPlayerName.trim();
    if (!name) return;
    setPlayers((current) => {
      const sugCashIn = mostCommonValue(current, "cashIn", DEFAULT_CASH_IN);
      const sugChips = mostCommonValue(current, "chipsBought", DEFAULT_CHIPS);
      return [...current, makePlayer(name, sugCashIn, sugChips, sugChips)];
    });
    setPendingPlayerName("");
  };

  const removePlayer = (playerId: string) => {
    setPlayers((current) => {
      setManualChipsPlayers((overrides) => {
        if (!overrides.has(playerId)) return overrides;
        const next = new Set(overrides);
        next.delete(playerId);
        return next;
      });
      setManualFinalChipsPlayers((overrides) => {
        if (!overrides.has(playerId)) return overrides;
        const next = new Set(overrides);
        next.delete(playerId);
        return next;
      });
      setHiddenFinalPlayers((hidden) => hidden.filter((p) => p.id !== playerId));
      return current.filter((p) => p.id !== playerId);
    });
  };

  const hidePlayerFromFinalList = (playerId: string) => {
    setPlayers((current) => {
      const player = current.find((p) => p.id === playerId);
      if (!player) return current;
      setHiddenFinalPlayers((hidden) => {
        if (hidden.some((e) => e.id === playerId)) return hidden;
        return [...hidden, { id: playerId, previousFinalChips: player.finalChips }];
      });
      return current;
    });
  };

  const restorePlayerToFinalList = (playerId: string) => {
    setHiddenFinalPlayers((hidden) => {
      const entry = hidden.find((p) => p.id === playerId);
      if (!entry) return hidden;
      setPlayers((current) =>
        current.map((p) => (p.id === playerId ? { ...p, finalChips: entry.previousFinalChips } : p)),
      );
      return hidden.filter((p) => p.id !== playerId);
    });
  };

  return {
    players,
    visibleFinalPlayers,
    hiddenFinalPlayers,
    hiddenFinalIds,
    pendingPlayerName,
    setPendingPlayerName,
    commitPendingPlayer,
    removePlayer,
    updatePlayerName,
    cashInputValue,
    beginCashEdit,
    changeCashEdit,
    endCashEdit,
    chipInputValue,
    beginChipEdit,
    changeChipEdit,
    endChipEdit,
    hidePlayerFromFinalList,
    restorePlayerToFinalList,
    totalsPreview,
    suggestedCashIn,
    suggestedChipsBought,
    chipValueDisplayUnit,
    exactChipValueUnit,
  };
}
